/**
 * User preferences — the single client seam for theme + date/time format +
 * chant rendering settings.
 *
 * Source of truth: for a logged-in user it's `user.preferences` (persisted to
 * the account via PATCH /api/account/preferences); for a guest it's
 * localStorage. `update()` writes to whichever is active and always mirrors to
 * localStorage, so a choice made while logged in survives logout and a guest's
 * choices can be adopted on first login. The provider also resolves the
 * light/dark/system choice to a concrete `data-theme` on <html> — the CSS only
 * ever sees "light" or "dark", never "system".
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_PREFERENCES,
  UserPreferences,
  parseUserPreferences,
  type PublicUser,
  type UserPreferences as Preferences,
  type UpdateUserPreferences as PreferencesPatch,
  type DateFormatPreference,
  type TimeFormatPreference,
} from '@vedaunion/shared';
import { apiFetch, type ApiError } from './api';
import { useMe } from './auth';

const STORAGE_KEY = 'vu:preferences';
const ME_KEY = ['me'] as const;

type MeData = { user: PublicUser | null; permissions: string[] };

// -- localStorage (guest) helpers ---------------------------------------------

function readGuest(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    // Same tolerant reader the server uses: one stale field must not wipe the
    // whole record (a hand-edited or older-build value here is normal).
    return parseUserPreferences(raw);
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function writeGuest(p: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* private mode / quota — ignore */
  }
}

/** Full base + shallow patch (chant / sankalpa / library merged one level deep), re-validated. */
function mergePrefs(base: Preferences, patch: PreferencesPatch): Preferences {
  return UserPreferences.parse({
    ...base,
    ...patch,
    chant: { ...base.chant, ...(patch.chant ?? {}) },
    sankalpa: { ...base.sankalpa, ...(patch.sankalpa ?? {}) },
    library: { ...base.library, ...(patch.library ?? {}) },
  });
}

/** Two PATCHES combined into one, same one-level-deep rule as `mergePrefs`.
 *  Used to COALESCE a burst of edits into a single request — see `update`. */
function mergePatch(a: PreferencesPatch, b: PreferencesPatch): PreferencesPatch {
  const out: PreferencesPatch = { ...a, ...b };
  if (a.chant || b.chant) out.chant = { ...(a.chant ?? {}), ...(b.chant ?? {}) };
  if (a.sankalpa || b.sankalpa) out.sankalpa = { ...(a.sankalpa ?? {}), ...(b.sankalpa ?? {}) };
  if (a.library || b.library) out.library = { ...(a.library ?? {}), ...(b.library ?? {}) };
  return out;
}

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

// -- context ------------------------------------------------------------------

interface PreferencesContextValue {
  prefs: Preferences;
  /** The concrete theme in effect ("system" already resolved). */
  resolvedTheme: 'light' | 'dark';
  isLoggedIn: boolean;
  update: (patch: PreferencesPatch) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const qc = useQueryClient();
  const user = me.data?.user ?? null;
  const isLoggedIn = !!user;

  const [guest, setGuest] = useState<Preferences>(() => readGuest());
  const [systemDark, setSystemDark] = useState<boolean>(() => prefersDark());

  // Track the OS scheme so `theme: system` reacts live.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const prefs = isLoggedIn && user ? user.preferences : guest;

  // Monotonic write counter — only the LATEST in-flight PATCH may write server
  // truth (or roll back) into the cache, so out-of-order responses from two
  // quick edits can't clobber a newer optimistic value.
  const seqRef = useRef(0);
  type Vars = { patch: PreferencesPatch; seq: number; prev: MeData | undefined };
  const mutation = useMutation<{ user: PublicUser }, ApiError, Vars>({
    mutationFn: ({ patch }) =>
      apiFetch<{ user: PublicUser }>('/api/account/preferences', { method: 'PATCH', body: patch }),
    onSuccess: (data, vars) => {
      if (vars.seq !== seqRef.current) return; // a newer write superseded this one
      const cur = qc.getQueryData<MeData>(ME_KEY);
      qc.setQueryData<MeData>(ME_KEY, { user: data.user, permissions: cur?.permissions ?? [] });
      writeGuest(data.user.preferences);
    },
    onError: (_e, vars) => {
      if (vars.seq === seqRef.current && vars.prev) qc.setQueryData<MeData>(ME_KEY, vars.prev);
    },
  });
  const mutate = mutation.mutate;

  /* ── Coalesced, SERIALISED server writes ──────────────────────────────────
   * A continuous control writes on every step: one drag of the audio-speed
   * slider used to fire dozens of concurrent PATCHes. The `seq` counter above
   * stops an out-of-order RESPONSE from clobbering the cache, but it cannot fix
   * the real damage — those requests are independent, so the SERVER may apply
   * them out of order too. Whichever it happens to persist last wins, and it
   * need not be the value the user let go of. That is the "sometimes it saves,
   * sometimes it doesn't" bug: the UI was right and the row behind it wasn't.
   *
   * So: keep the optimistic local write instant (the slider must not lag), and
   * behind it hold ONE pending patch, send at most ONE request at a time, and
   * merge anything that arrives while a request is in flight into the next one.
   * A burst becomes a single PATCH carrying the final value, and there is never
   * more than one writer, so ordering cannot be ambiguous.
   *
   * `pagehide`/`visibilitychange` flush, because on a phone "change it, then
   * switch apps" is the normal way an edit ends and a 350 ms debounce would
   * otherwise still be waiting. That flush is a best effort — a request the
   * browser tears down with the page is still lost — but the window shrinks
   * from "the whole debounce" to "the request itself", and the value is already
   * in localStorage either way, so the reader shows the right thing on return
   * even when the server write did not make it. */
  const pendingRef = useRef<PreferencesPatch | null>(null);
  const pendingPrevRef = useRef<MeData | undefined>(undefined);
  const inFlightRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const WRITE_DELAY = 350;

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (inFlightRef.current) return;      // the settle handler will pick it up
    const patch = pendingRef.current;
    if (!patch) return;
    pendingRef.current = null;
    inFlightRef.current = true;
    mutate({ patch, seq: ++seqRef.current, prev: pendingPrevRef.current },
      { onSettled: () => { inFlightRef.current = false; if (pendingRef.current) flush(); } });
  }, [mutate]);

  const queue = useCallback((patch: PreferencesPatch, prev: MeData | undefined) => {
    if (pendingRef.current === null) pendingPrevRef.current = prev;
    pendingRef.current = pendingRef.current ? mergePatch(pendingRef.current, patch) : patch;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(flush, WRITE_DELAY);
  }, [flush]);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHide);
      flush();                            // unmounting must not drop an edit
    };
  }, [flush]);

  const update = useCallback(
    (patch: PreferencesPatch) => {
      if (isLoggedIn && user) {
        const prev = qc.getQueryData<MeData>(ME_KEY);
        // Merge onto the LATEST cache prefs (not the render-time user) so rapid
        // successive edits accumulate instead of overwriting each other.
        const base = prev?.user?.preferences ?? user.preferences;
        const optimistic = mergePrefs(base, patch);
        qc.setQueryData<MeData>(ME_KEY, {
          user: { ...user, preferences: optimistic },
          permissions: prev?.permissions ?? [],
        });
        writeGuest(optimistic);
        queue(patch, prev);
      } else {
        const next = mergePrefs(guest, patch);
        setGuest(next);
        writeGuest(next);
        try {
          sessionStorage.setItem('vu:guestTouched', '1');
        } catch {
          /* ignore */
        }
      }
    },
    [isLoggedIn, user, guest, qc, queue],
  );

  // Adopt-on-login: if the account is still at defaults AND the guest changed
  // prefs DURING THIS pre-login session (sessionStorage flag, not stale
  // localStorage from a previous user on a shared browser), carry them up once.
  const adopted = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || !user || adopted.current) return;
    adopted.current = true;
    let touched = false;
    try {
      touched = sessionStorage.getItem('vu:guestTouched') === '1';
    } catch {
      /* ignore */
    }
    const accountIsDefault =
      JSON.stringify(user.preferences) === JSON.stringify(DEFAULT_PREFERENCES);
    const guestCustom = JSON.stringify(guest) !== JSON.stringify(DEFAULT_PREFERENCES);
    if (touched && accountIsDefault && guestCustom) {
      const prev = qc.getQueryData<MeData>(ME_KEY);
      qc.setQueryData<MeData>(ME_KEY, {
        user: { ...user, preferences: guest },
        permissions: prev?.permissions ?? [],
      });
      mutate({
        patch: {
          theme: guest.theme,
          dateFormat: guest.dateFormat,
          timeFormat: guest.timeFormat,
          chant: guest.chant,
          library: guest.library,
        },
        seq: ++seqRef.current,
        prev,
      });
    }
    try {
      sessionStorage.removeItem('vu:guestTouched');
    } catch {
      /* ignore */
    }
  }, [isLoggedIn, user, guest, qc, mutate]);

  const resolvedTheme: 'light' | 'dark' =
    prefs.theme === 'system' ? (systemDark ? 'dark' : 'light') : prefs.theme;

  // Apply the resolved theme to <html> + keep the browser chrome colour in sync.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolvedTheme;
    try {
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]#vu-theme-color');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        meta.id = 'vu-theme-color';
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', resolvedTheme === 'dark' ? '#171226' : '#faf7f0');
    } catch {
      /* head not ready — the inline no-flash script already set the attribute */
    }
  }, [resolvedTheme]);

  const value = useMemo<PreferencesContextValue>(
    () => ({ prefs, resolvedTheme, isLoggedIn, update }),
    [prefs, resolvedTheme, isLoggedIn, update],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within a PreferencesProvider');
  return ctx;
}

// -- date / time formatting per preference ------------------------------------

/** Resolve the browser's default day/month order once. */
function localeDayFirst(): boolean {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'numeric' }).formatToParts(
      new Date(2000, 0, 2),
    );
    const dayIdx = parts.findIndex((p) => p.type === 'day');
    const monthIdx = parts.findIndex((p) => p.type === 'month');
    return dayIdx < monthIdx;
  } catch {
    return true;
  }
}

export function formatDatePref(
  iso: string | number | Date | null | undefined,
  fmt: DateFormatPreference,
  opts: { withYear?: boolean; long?: boolean } = {},
): string {
  if (iso == null) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const withYear = opts.withYear ?? true;
  const y = d.getFullYear();
  const mNum = String(d.getMonth() + 1).padStart(2, '0');
  const dNum = String(d.getDate()).padStart(2, '0');

  if (fmt === 'iso') return withYear ? `${y}-${mNum}-${dNum}` : `${mNum}-${dNum}`;

  const monthName = d.toLocaleDateString('en-GB', { month: opts.long ? 'long' : 'short' });
  const dayFirst = fmt === 'dmy' ? true : fmt === 'mdy' ? false : fmt === 'ymd' ? false : localeDayFirst();

  if (fmt === 'ymd') return withYear ? `${y} ${monthName} ${d.getDate()}` : `${monthName} ${d.getDate()}`;
  if (dayFirst) return `${d.getDate()} ${monthName}${withYear ? ` ${y}` : ''}`;
  return `${monthName} ${d.getDate()}${withYear ? `, ${y}` : ''}`;
}

export function formatTimePref(
  iso: string | number | Date | null | undefined,
  fmt: TimeFormatPreference,
): string {
  if (iso == null) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hour12 = fmt === 'h12' ? true : fmt === 'h24' ? false : undefined;
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  });
}

/** Convenience: formatters bound to the current user's preferences. */
export function useDateTimeFormat() {
  const { prefs } = usePreferences();
  return useMemo(
    () => ({
      date: (iso: string | number | Date | null | undefined, opts?: { withYear?: boolean; long?: boolean }) =>
        formatDatePref(iso, prefs.dateFormat, opts),
      time: (iso: string | number | Date | null | undefined) => formatTimePref(iso, prefs.timeFormat),
    }),
    [prefs.dateFormat, prefs.timeFormat],
  );
}
